import React, {useState, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'
import {ThryvAndKeapHeader} from './order-details'
import {SchedulerAppointmentComponent} from '../components/keap-scheduler-v2/scheduler-appointment'
import {SchdulerSuccessComponent} from '../components/keap-scheduler-v2/scheduler-success'
import {getKickoffCallLabelFromCart} from '../components/keap-scheduler-v2/kp-11Link-service'
import {
  KCPPaymentDetails,
  computeTabTotal,
  getCoreTabItemCount,
} from '../components/subscription-summary'
import {GradientTabs, GradientTab} from '../core/shared/GradientTabs'
import {formatPrice} from '../utils/currency-format'
import {isPartnerOneToManyLink} from '../utils/partner-scheduling'
import '../styles/formScreensStyles.css'

const NON_WORKFORCE_KICKOFF_CODES = [
  'seobase-5-thryv-m2m',
  'tl-mc-5-thryv-m2m',
  'addon-socialadsbronze-5-m2m',
  'addon-socialadsgold-5-m2m',
  'addon-socialadssilver-5-m2m',
  'addon-6-mc-socialcontentgen-m2m',
]

const isWorkforceItem = item => {
  const code = item?.code || ''
  const name = item?.name?.toLowerCase?.() || ''
  return code.startsWith('wc_') || name.includes('workforce center')
}

const getFlattenedCartItems = ({centers = [], apps = [], oneTimeFees = []}) =>
  [...centers, ...apps, ...oneTimeFees]
    .filter(Boolean)
    .flatMap(item => [item, ...(item?.addons || [])])

const isWorkforceOnlyKickoffCart = ({
  centers = [],
  apps = [],
  oneTimeFees = [],
}) => {
  const hasWorkforceCenter = [...centers, ...apps].some(isWorkforceItem)
  const hasNonWorkforceKickoffItem = getFlattenedCartItems({
    centers,
    apps,
    oneTimeFees,
  }).some(item => NON_WORKFORCE_KICKOFF_CODES.includes(item?.code))

  return hasWorkforceCenter && !hasNonWorkforceKickoffItem
}

export const AppointmentDetailsComponent = () => {
  const navData = useLocation()
  const navigate = useNavigate()
  const [isScheduleCompleted, setScheduleCompletedStatus] = useState(false)
  const [isSkipped, setIsSkipped] = useState(false)
  const [isRescheduleRequested, setIsRescheduleRequested] = useState(false)
  const [activeRightTab, setActiveRightTab] = useState('today')
  const {isMobileViewEnable} = useSelector(state => state.screenView)

  useEffect(() => {
    const gradient = 'linear-gradient(to bottom, #171d37, #6474a9)'
    document.body.style.background = gradient
    document.body.style.minHeight = '100vh'
    if (!isMobileViewEnable) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.background = ''
      document.body.style.minHeight = ''
      document.body.style.overflow = ''
    }
  }, [isMobileViewEnable])
  const [scheduleKickOffCallDetails, setScheduledKickOffCallDetails] =
    useState(null)
  const [scheduleCreativeCallDetails, setScheduledCreativeCallDetails] =
    useState(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Initialize with default structure to prevent errors
  const [structuredCart, setStructuredCart] = useState({
    centers: [],
    apps: [],
    oneTimeFees: [],
    plans: [],
  })

  useEffect(() => {
    console.log('=== APPOINTMENT PAGE DEBUG ===')
    console.log('navData:', navData)
    console.log('navData.state:', navData?.state)
    console.log('navData.state.orderDetail:', navData?.state?.orderDetail)
    console.log(
      'navData.state.accountInformation:',
      navData?.state?.accountInformation,
    )
    console.log(
      'accountInformation.email:',
      navData?.state?.accountInformation?.email,
    )
    console.log(
      'accountInformation.address:',
      navData?.state?.accountInformation?.address,
    )

    // The orderDetail is already formatted from order-details page
    if (navData?.state?.orderDetail) {
      const orderDetail = navData.state.orderDetail
      console.log('Setting structuredCart with orderDetail:', orderDetail)

      // Check if orderDetail has productSub with the structured data
      if (orderDetail.productSub) {
        console.log('Found productSub:', orderDetail.productSub)

        // Extract centers, apps, and oneTimeFees from productSub
        const parentSubs = orderDetail.productSub.parentSubscriptions || []
        const childSubs = orderDetail.productSub.childSubscriptions || []

        // Combine all subscriptions
        const allSubs = [...parentSubs, ...childSubs]

        // Separate into centers, apps, and oneTimeFees based on the plans array
        const centers = []
        const apps = []
        const oneTimeFees = []

        // Process all subscriptions and their addons
        allSubs.forEach(sub => {
          // Add the subscription itself
          if (sub.code?.includes('center') || sub.type === 'center') {
            centers.push(sub)
          } else if (sub.code?.includes('fee') || sub.type === 'oneTimeFee') {
            oneTimeFees.push(sub)
          } else {
            apps.push(sub)
          }

          // Process addons
          if (sub.addons && Array.isArray(sub.addons)) {
            sub.addons.forEach(addon => {
              if (addon.code?.includes('fee') || addon.type === 'oneTimeFee') {
                oneTimeFees.push(addon)
              } else {
                apps.push(addon)
              }
            })
          }
        })

        const structured = {
          centers,
          apps,
          oneTimeFees,
          plans: orderDetail.plans || [],
        }

        console.log('Structured cart:', structured)
        setStructuredCart(structured)
      } else {
        // Fallback: use the orderDetail as-is and add empty arrays
        console.log('No productSub found, using orderDetail with defaults')
        setStructuredCart({
          centers: [],
          apps: [],
          oneTimeFees: [],
          plans: orderDetail.plans || [],
          ...orderDetail,
        })
      }
    } else {
      console.warn('No orderDetail found in navData.state!')
    }
  }, [navData])

  useEffect(() => {
    console.log('structuredCart updated:', structuredCart)
    console.log('structuredCart.apps:', structuredCart?.apps)
    console.log('structuredCart.centers:', structuredCart?.centers)
    console.log('structuredCart.oneTimeFees:', structuredCart?.oneTimeFees)
  }, [structuredCart])

  useEffect(() => {
    // Scroll to top when initial load completes
    if (isInitialLoad && structuredCart.plans.length > 0) {
      window.scrollTo({top: 0, behavior: 'smooth'})
      setIsInitialLoad(false)
    }
  }, [structuredCart, isInitialLoad])

  useEffect(() => {
    // Scroll to top when schedule is confirmed
    if (isScheduleCompleted) {
      window.scrollTo({top: 0, behavior: 'smooth'})
    }
  }, [isScheduleCompleted])

  useEffect(() => {
    if (!isSkipped) {
      return
    }

    const isSkipStillAllowed = isWorkforceOnlyKickoffCart(structuredCart)
    if (!isSkipStillAllowed) {
      setIsSkipped(false)
      setScheduleCompletedStatus(false)
      setScheduledKickOffCallDetails(null)
    }
  }, [structuredCart, isSkipped])

  const handleScheduleSuccessStatus = () => {
    setIsSkipped(false)
    setIsRescheduleRequested(false)
    setScheduleCompletedStatus(true)
  }

  // Back from the confirmation to pick another slot. The booked call stays in
  // place until the new one is confirmed (book first, then release the old).
  const handleReschedule = () => {
    setIsSkipped(false)
    setIsRescheduleRequested(true)
    setScheduleCompletedStatus(false)
  }

  const handleSkipScheduling = () => {
    sessionStorage.removeItem('scheduleKickOffCallDetails')
    sessionStorage.removeItem('scheduleCreativeCallDetails')
    setIsSkipped(true)
    setScheduleCompletedStatus(true)
  }

  const handleContinue = () => {
    navigate('/client-payment', {
      state: {
        ...navData.state,
        scheduleKickOffCallDetails,
        scheduleCreativeCallDetails,
        kickoffCallLabel: getKickoffCallLabelFromCart(structuredCart),
      },
    })
  }

  // Check if website product is present in  navData.state.orderDetail
  const isProductInOrder = (items, productCode) => {
    for (const item of items) {
      if (item.code === productCode) {
        return item
      }
      if (item.addons && item.addons.length > 0) {
        const foundAsAddon = item.addons.find(
          addon => addon.code === productCode,
        )

        if (foundAsAddon) {
          return foundAsAddon
        }
      }
    }
    return null
  }
  // Determine which calls exist based on the cart
  const influencerName = navData.state?.keapPartnerLinkDetail?.influencer_name
  const isKeapPartner =
    !influencerName && !!navData.state?.keapPartnerLinkDetail
  const websiteInCart = isProductInOrder(
    navData?.state?.orderDetail?.productSub?.childSubscriptions || [],
    'ent-5-website',
  )
  const isKickOffCallExist =
    (influencerName !== null &&
      influencerName !== undefined &&
      influencerName !== '') ||
    isPartnerOneToManyLink(
      navData.state?.keapPartnerLinkDetail,
      navData.state?.linkType,
    ) ||
    (isKeapPartner && !!websiteInCart)
  const isCreativeCallExist = false
  const todayTotal = (() => {
    const t = computeTabTotal({
      paymentDetailType: 'today',
      cartDetailObj: navData.state?.orderDetail,
      couponList: navData.state?.couponList,
      country: navData?.state?.country,
    })
    return t != null
      ? `${formatPrice({amount: t, country: navData?.state?.country})} + Tax`
      : undefined
  })()

  return (
    <>
      <ThryvAndKeapHeader
        totalAmount={isMobileViewEnable ? undefined : todayTotal}
      />
      {isMobileViewEnable ? (
        <section
          style={{
            display: 'flex',
            flexDirection: 'column',
            paddingInline: '16px',
            paddingBottom: '24px',
            gap: '16px',
          }}
        >
          <section style={{display: 'flex', flexDirection: 'column'}}>
            <p
              style={{
                paddingTop: '20px',
                fontWeight: 700,
                fontFamily: 'Open Sans',
                fontSize: '20px',
                color: 'white',
              }}
            >
              Schedule Appointment
            </p>
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #ECEEF1',
                borderRadius: '12px',
                boxShadow: '0px 2px 4px 0px #00000040',
                padding: '24px',
                width: '100%',
              }}
            >
              {isScheduleCompleted ? (
                <SchdulerSuccessComponent
                  isKickOffCallExist={isKickOffCallExist}
                  isCreativeCallExist={isCreativeCallExist}
                  scheduleCreativeCallDetails={scheduleCreativeCallDetails}
                  scheduleKickOffCallDetails={scheduleKickOffCallDetails}
                  isSkipped={isSkipped}
                  accountInformation={navData.state?.accountInformation}
                  handleModalClose={handleContinue}
                  onReschedule={handleReschedule}
                  structuredCart={structuredCart}
                />
              ) : (
                <SchedulerAppointmentComponent
                  isKickOffCallExist={isKickOffCallExist}
                  isCreativeCallExist={isCreativeCallExist}
                  scheduleCreativeCallDetails={scheduleCreativeCallDetails}
                  scheduleKickOffCallDetails={scheduleKickOffCallDetails}
                  setScheduledCreativeCallDetails={
                    setScheduledCreativeCallDetails
                  }
                  setScheduledKickOffCallDetails={
                    setScheduledKickOffCallDetails
                  }
                  handleScheduleSuccessStatus={handleScheduleSuccessStatus}
                  startInRescheduleMode={isRescheduleRequested}
                  handleSkipScheduling={handleSkipScheduling}
                  accountInformation={navData.state?.accountInformation}
                  transitionData={navData}
                  structuredCart={structuredCart}
                />
              )}
            </div>
          </section>
          <section
            style={{
              background: 'white',
              paddingBlock: '20px',
              paddingInline: '20px',
              borderRadius: '10px',
            }}
          >
            <p
              style={{
                paddingTop: '20px',
                fontWeight: 700,
                fontFamily: 'Open Sans',
                fontSize: '20px',
                marginBottom: '32px',
                color: 'black',
              }}
            >
              Payment Details
            </p>
            <section
              style={{display: 'flex', flexDirection: 'column', gap: '24px'}}
            >
              <KCPPaymentDetails
                country={navData?.state?.country}
                locationNames={navData.state?.locationNames}
                cartDetailObj={navData.state?.orderDetail}
                paymentDetailType="monthly"
                currentScreen={'appointment'}
              />
              <KCPPaymentDetails
                country={navData?.state?.country}
                locationNames={navData.state?.locationNames}
                paymentDetailType="today"
                cartDetailObj={navData.state?.orderDetail}
                currentScreen={'appointment'}
                couponList={navData.state?.couponList}
              />
            </section>
          </section>
        </section>
      ) : (
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: '293fr 137fr',
            alignItems: 'stretch',
            width: '100%',
            gap: '26px',
            boxSizing: 'border-box',
            paddingInline: '24px',
          }}
        >
          {/* Left side - Appointment Scheduler */}
          <section
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '100%',
              maxHeight: 'calc(100vh - 108px)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: 'calc(100vh - 108px)',
                maxHeight: 'calc(100vh - 108px)',
                overflow: 'hidden',
              }}
            >
              {/* CARD HEADER */}
              <div
                style={{
                  paddingInline: '20px',
                  paddingBlock: '20px',
                  flexShrink: 0,
                }}
              >
                <p className="payment-method-label" style={{fontSize: '18px'}}>
                  Schedule Appointment
                </p>
              </div>
              {/* CARD CONTENT */}
              <div
                className="payment-form-scroll"
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  padding: '20px',
                  scrollbarGutter: 'stable',
                  background: '#DDE1EA',
                  borderRadius: '10px 10px 0px 0px',
                }}
              >
                {isScheduleCompleted ? (
                  <SchdulerSuccessComponent
                    isKickOffCallExist={isKickOffCallExist}
                    isCreativeCallExist={isCreativeCallExist}
                    scheduleCreativeCallDetails={scheduleCreativeCallDetails}
                    scheduleKickOffCallDetails={scheduleKickOffCallDetails}
                    isSkipped={isSkipped}
                    accountInformation={navData.state?.accountInformation}
                    handleModalClose={handleContinue}
                    onReschedule={handleReschedule}
                    structuredCart={structuredCart}
                  />
                ) : (
                  <div
                    style={{
                      background: 'white',
                      borderRadius: '10px',
                      padding: '16px',
                    }}
                  >
                    <SchedulerAppointmentComponent
                      isKickOffCallExist={isKickOffCallExist}
                      isCreativeCallExist={isCreativeCallExist}
                      scheduleCreativeCallDetails={scheduleCreativeCallDetails}
                      scheduleKickOffCallDetails={scheduleKickOffCallDetails}
                      setScheduledCreativeCallDetails={
                        setScheduledCreativeCallDetails
                      }
                      setScheduledKickOffCallDetails={
                        setScheduledKickOffCallDetails
                      }
                      handleScheduleSuccessStatus={handleScheduleSuccessStatus}
                      startInRescheduleMode={isRescheduleRequested}
                      handleSkipScheduling={handleSkipScheduling}
                      accountInformation={navData.state?.accountInformation}
                      transitionData={navData}
                      structuredCart={structuredCart}
                    />
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Right side - Payment Details Summary */}
          <section
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              height: 'calc(100vh - 108px)',
              maxHeight: 'calc(100vh - 108px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* CURRENCY DISCLAIMER */}
            <p
              style={{
                fontFamily: 'Open Sans',
                fontSize: '12px',
                color: '#4D4D4D',
                margin: '0',
                paddingInline: '20px',
                paddingBlock: '10px',
                flexShrink: 0,
              }}
            >
              All prices displayed in{' '}
              {navData?.state?.country === 'CA'
                ? 'CAD'
                : navData?.state?.country === 'AU'
                  ? 'AUD'
                  : navData?.state?.country === 'NZ'
                    ? 'NZD'
                    : navData?.state?.country === 'GB'
                      ? 'GBP'
                      : 'USD'}
            </p>
            {/* TABS HEADER */}
            <div style={{flexShrink: 0, paddingInline: '10px'}}>
              <GradientTabs
                activeTab={activeRightTab}
                onChange={setActiveRightTab}
                variant="payment"
              >
                <GradientTab id="today">
                  <div className="label">
                    <p
                      className={`tab-label-title${activeRightTab === 'today' ? ' active' : ''}`}
                    >
                      Due Today{' '}
                      <span>
                        (
                        {getCoreTabItemCount(
                          navData.state?.orderDetail,
                          'today',
                        ) ??
                          (navData.state?.orderDetail?.plans || []).filter(
                            p =>
                              ![
                                'addon-5-signatures_usage',
                                'app-websitebuilder',
                                'cc_basicpls-5-thryv-m2m',
                                'thryvprfee',
                              ].includes(p.code),
                          ).length}{' '}
                        Items)
                      </span>
                    </p>
                  </div>
                  <p
                    className={`tab-price${activeRightTab === 'today' ? ' active' : ''}`}
                  >
                    {(() => {
                      const t = computeTabTotal({
                        paymentDetailType: 'today',
                        cartDetailObj: navData.state?.orderDetail,
                        couponList: navData.state?.couponList,
                        country: navData?.state?.country,
                      })
                      return t != null
                        ? `${formatPrice({amount: t, country: navData?.state?.country})} + Tax`
                        : ''
                    })()}
                  </p>
                </GradientTab>
                <GradientTab id="monthly">
                  <div className="label blue">
                    <p
                      className={`tab-label-title${activeRightTab === 'monthly' ? ' active' : ''}`}
                    >
                      {(() => {
                        const annualT = computeTabTotal({
                          paymentDetailType: 'annual',
                          cartDetailObj: navData.state?.orderDetail,
                          country: navData?.state?.country,
                        })
                        const monthlyT = computeTabTotal({
                          paymentDetailType: 'monthly',
                          cartDetailObj: navData.state?.orderDetail,
                          country: navData?.state?.country,
                        })
                        return annualT > 0 && monthlyT === 0
                          ? 'Due Annually'
                          : 'Due Monthly'
                      })()}{' '}
                      <span>
                        (
                        {(() => {
                          const annualT = computeTabTotal({
                            paymentDetailType: 'annual',
                            cartDetailObj: navData.state?.orderDetail,
                            country: navData?.state?.country,
                          })
                          const monthlyT = computeTabTotal({
                            paymentDetailType: 'monthly',
                            cartDetailObj: navData.state?.orderDetail,
                            country: navData?.state?.country,
                          })
                          const isAnnualView = annualT > 0 && monthlyT === 0
                          const isAnnualItem = p =>
                            String(p.code || '')
                              .toLowerCase()
                              .includes('annual') ||
                            String(p.name || '')
                              .toLowerCase()
                              .includes('annual') ||
                            String(p.name || '')
                              .toLowerCase()
                              .includes('yearly')
                          const coreCount = getCoreTabItemCount(
                            navData.state?.orderDetail,
                            isAnnualView ? 'annual' : 'monthly',
                          )
                          if (coreCount !== null) return coreCount
                          return (
                            navData.state?.orderDetail?.plans || []
                          ).filter(
                            p =>
                              ![
                                'addon-5-signatures_usage',
                                'app-websitebuilder',
                                'cc_basicpls-5-thryv-m2m',
                                'thryvprfee',
                              ].includes(p.code) &&
                              p.prodType !== 'OneTimeFees' &&
                              p.prodType !== 'oneTimeFee' &&
                              p.section !== 'oneTimeFees' &&
                              p.groupKey !== 'oneTimeFees' &&
                              (isAnnualView
                                ? isAnnualItem(p)
                                : !isAnnualItem(p)),
                          ).length
                        })()}{' '}
                        Items)
                      </span>
                    </p>
                  </div>
                  <p
                    className={`tab-price${activeRightTab === 'monthly' ? ' active' : ''}`}
                  >
                    {(() => {
                      const annualT = computeTabTotal({
                        paymentDetailType: 'annual',
                        cartDetailObj: navData.state?.orderDetail,
                        country: navData?.state?.country,
                      })
                      const monthlyT = computeTabTotal({
                        paymentDetailType: 'monthly',
                        cartDetailObj: navData.state?.orderDetail,
                        country: navData?.state?.country,
                      })
                      const t =
                        annualT > 0 && monthlyT === 0 ? annualT : monthlyT
                      return t != null
                        ? `${formatPrice({amount: t, country: navData?.state?.country})} + Tax`
                        : ''
                    })()}
                  </p>
                </GradientTab>
              </GradientTabs>
            </div>
            {/* SCROLLABLE PRODUCTS */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                className="payment-form-scroll"
                style={{
                  position: 'absolute',
                  inset: 0,
                  overflowY: 'auto',
                  padding: '10px',
                  scrollbarGutter: 'stable',
                  background: '#DDE1EA',
                  borderRadius: '10px',
                  boxShadow: '0px 0px 22px 0px #00000040 inset',
                }}
              >
                {activeRightTab === 'today' ? (
                  <KCPPaymentDetails
                    country={navData?.state?.country}
                    locationNames={navData.state?.locationNames}
                    paymentDetailType="today"
                    cartDetailObj={navData.state?.orderDetail}
                    currentScreen={'appointment'}
                    couponList={navData.state?.couponList}
                    hideHeading
                  />
                ) : (
                  <KCPPaymentDetails
                    country={navData?.state?.country}
                    locationNames={navData.state?.locationNames}
                    cartDetailObj={navData.state?.orderDetail}
                    paymentDetailType="monthly"
                    currentScreen={'appointment'}
                    hideHeading
                  />
                )}
              </div>
            </div>
          </section>
        </section>
      )}
    </>
  )
}
